/**
 * `AdresseFelder` — gemeinsamer Formularblock Firma/Name/Straße/Zusatz/PLZ+Ort/Land (Adressmodell,
 * WARTESCHLANGE Punkt 7, PR4). Ersetzt die bisher SECHS unabhaengig gebauten Straße/PLZ/Ort-
 * Formulare (`ui/VeranstaltungsDatenDrawer.tsx` x2 — „Adresse dieses Auftrags" UND „Lieferadresse",
 * `pages/event-detail.tsx`, `ui/auftrag/AuftragEckdatenKarte.tsx#AdressBlock`,
 * `pages/public-rechnungsadresse.tsx`, `pages/offer-public.tsx`) — genau der in
 * `docs/KONZEPT-AUFTRAGSADRESSE.md` § 1 benannte Missstand „keine wiederverwendbare
 * Adress-Komponente existiert", nach dieser Erweiterung erst recht. Feldreihenfolge nach dem
 * freigegebenen Mockup (Antoine, 15.09.2026 "Mock up sind alle freigegeben") + DIN 5008: Firma/Name
 * (Reihenfolge je Aufrufer, s. `firmaName` unten) → Straße und Hausnummer → Adresszusatz →
 * PLZ+Ort (Reihe nebeneinander) → Land (volle Breite, Dropdown).
 *
 * Baut ausschliesslich auf `Input`/`Select` (`ui/components.tsx`) auf — kein rohes
 * `input`/`select`-Element in dieser Datei, damit die Design-Ratsche
 * (`apps/web/test/design-komponenten-bestand.test.ts`) durch die Einfuehrung dieser Komponente
 * NICHT waechst (sie sinkt sogar, weil sie bestehende rohe Stellen in den Aufrufern ablöst).
 *
 * ## Zwei Interaktionsmuster, EINE Komponente
 *
 * Die sechs Aufrufer speichern auf zwei verschiedene Arten (im Bestand bereits etabliert, hier
 * nicht neu erfunden):
 *   - **Autosave onBlur** (`VeranstaltungsDatenDrawer.tsx`, `AuftragEckdatenKarte.tsx`,
 *     `event-detail.tsx`s "Vorgang bearbeiten"-Drawer): unkontrollierte Inputs mit `key`-Reset auf
 *     den aktuellen Wert (Muster `key={\`eas-${eventAddressStreet ?? ""}\`}` in
 *     `VeranstaltungsDatenDrawer.tsx`), Speichern erst beim Verlassen des Felds. `commitOn="blur"`.
 *   - **Kontrolliert, jede Tastatureingabe** (`public-rechnungsadresse.tsx`, `offer-public.tsx`):
 *     lokaler React-State pro Feld, `value`/`onChange`. `commitOn="change"` (Standard).
 * `onFeldChange(feld, wert)` ist in beiden Faellen derselbe Callback — der Aufrufer entscheidet,
 * OB er daraus sofort einen PATCH macht (autosave) oder nur `setState` (kontrolliert), die
 * Komponente selbst entscheidet nur WANN sie ihn auslöst (blur vs. jede Aenderung).
 *
 * ## Zwei Anzeige-Varianten
 *
 * `variante="beschriftet"` (Standard, Betreiber-Ansicht): sichtbare `.lbl`-Beschriftung ueber jedem
 * Feld, wie im Bearbeiten-Drawer/der Eckdaten-Karte. `variante="platzhalter"` (Gast-Formulare,
 * `public-rechnungsadresse.tsx`/`offer-public.tsx`): Platzhaltertext statt sichtbarem Label,
 * dasselbe dichte Formular-Muster wie dort heute schon (kein Redesign dieser beiden Seiten in
 * dieser Iteration) — mit `aria-label` fuer Screenreader, da kein sichtbares `<label>`.
 *
 * ## Firma/Zusatz/Land NUR wenn der Aufrufer sie anbietet
 *
 * `firmaName` steuert Sichtbarkeit UND Reihenfolge der Firma/Name-Zeile (§ 2.4 Konzept-Dokument:
 * Betreiber-Ansicht "Firma, Name" vs. Gast-Ansicht "Name, Firma", beides laut Mockup verifiziert).
 * `zeigeZusatz`/`zeigeLand` sind eigene Schalter (nicht an `firmaName` gekoppelt). Bis sql/485
 * (23.09.2026) mussten die beiden Gast-Formulare (`public-rechnungsadresse.tsx`, `offer-public.tsx`)
 * sie ausblenden, weil ihre oeffentlichen Routen Zusatz/Land nicht annahmen; seitdem zeigen alle
 * Aufrufer beide Felder. Die Schalter bleiben fuer kuenftige Formulare ohne diese Felder.
 */
import { Input, Select } from "./components";

export interface AdresseWerte {
  company?: string | null;
  name?: string | null;
  street: string | null;
  addition: string | null;
  zip: string | null;
  city: string | null;
  countryCode: string | null;
}

export type AdresseFeld = keyof AdresseWerte;

export interface AdresseFelderLabels {
  firma?: string;
  name?: string;
  street?: string;
  addition?: string;
  zip?: string;
  city?: string;
  land?: string;
}

const STANDARD_LABELS: Required<AdresseFelderLabels> = {
  firma: "Firma",
  name: "Name",
  street: "Straße und Hausnummer",
  addition: "Adresszusatz",
  zip: "PLZ",
  city: "Ort",
  land: "Land",
};

/** Sentinel fuer "kein Land ausgewaehlt" im Radix-`Select` (leerer String ist dort kein gueltiger
 *  Item-Wert) — dasselbe lokale Muster wie `KEIN_WERT` in `pages/settings.tsx`/`pages/event-detail.tsx`. */
const KEIN_LAND = "__kein_land__";

/** `data-testid`-Suffixe: DEUTSCH, damit die bereits etablierten Test-IDs fuer Straße/PLZ/Ort
 *  (`*-strasse`/`*-plz`/`*-ort`, s. `VeranstaltungsDatenDrawer.tsx`/`AuftragEckdatenKarte.tsx`/
 *  `event-detail.tsx` VOR dieser Komponente) unveraendert bleiben — kein stiller Bruch bestehender
 *  Unit-/E2E-Selektoren (`apps/web/test/auftrag-eckdaten-adresse.test.tsx`,
 *  `apps/e2e/tests/auftragsadresse.spec.ts`, `apps/e2e/tests/auftrags-dashboard-lieferadresse.spec.ts`
 *  u. a.). Die drei neuen Felder (Firma/Zusatz/Land) folgen demselben Sprachmuster wie der
 *  restliche Testid-Bestand im Repo (Deutsch, nicht die internen TS-Feldnamen). */
const TESTID_SUFFIX: Record<Exclude<AdresseFeld, "countryCode">, string> = {
  company: "firma",
  name: "name",
  street: "strasse",
  addition: "zusatz",
  zip: "plz",
  city: "ort",
};

const STANDARD_LABEL_KLASSE = "mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface";

export function AdresseFelder({
  werte,
  onFeldChange,
  laender,
  firmaName,
  zeigeZusatz = true,
  zeigeLand = true,
  variante = "beschriftet",
  commitOn = "change",
  labels,
  labelClassName,
  testIdPrefix,
  disabled = false,
}: {
  werte: AdresseWerte;
  onFeldChange: (feld: AdresseFeld, wert: string) => void;
  /** Land-Liste — Aufrufer reicht `LAENDER` aus `lib/laender.ts` durch (kein impliziter Import
   *  hier, damit diese Komponente nicht an einer bestimmten Liste haengt). Nur noetig, wenn
   *  `zeigeLand`. */
  laender?: { code: string; name: string }[];
  /** "betreiber": Firma vor Name (Betreiber-Ansicht "du"). "gast": Name vor Firma (Gast-Formulare
   *  "Sie", § 2.4 Konzept-Dokument). `undefined`: Firma/Name-Zeile ganz ausgeblendet. */
  firmaName?: "betreiber" | "gast";
  zeigeZusatz?: boolean;
  zeigeLand?: boolean;
  variante?: "beschriftet" | "platzhalter";
  /** "blur": unkontrollierte Inputs, Callback erst beim Verlassen des Felds (Autosave-Idiom).
   *  "change" (Standard): kontrollierte Inputs, Callback bei jeder Tastatureingabe. */
  commitOn?: "blur" | "change";
  labels?: AdresseFelderLabels;
  /** Ueberschreibt die Standard-Label-Klasse (`variante="beschriftet"` NUR) — manche Aufrufer
   *  (`AuftragEckdatenKarte.tsx`) haben ein eigenes, leichteres Label-Muster (`ROW_LABEL` aus
   *  `eckdaten-stile.ts`) statt der halbfetten Standard-Beschriftung der Bearbeiten-Drawer. */
  labelClassName?: string;
  testIdPrefix: string;
  disabled?: boolean;
}) {
  const L = { ...STANDARD_LABELS, ...labels };
  const blur = commitOn === "blur";
  const labelKlasse = labelClassName ?? STANDARD_LABEL_KLASSE;

  function feld(key: Exclude<AdresseFeld, "countryCode">, label: string) {
    const wert = (werte[key] as string | null | undefined) ?? "";
    const testId = `${testIdPrefix}-${TESTID_SUFFIX[key]}`;
    const platzhalter = variante === "platzhalter";
    const input = blur ? (
      <Input
        key={`${testId}-${wert}`}
        data-testid={testId}
        disabled={disabled}
        defaultValue={wert}
        onBlur={(e) => onFeldChange(key, e.target.value)}
        aria-label={platzhalter ? label : undefined}
        placeholder={platzhalter ? label : undefined}
      />
    ) : (
      <Input
        data-testid={testId}
        disabled={disabled}
        value={wert}
        onChange={(e) => onFeldChange(key, e.target.value)}
        aria-label={platzhalter ? label : undefined}
        placeholder={platzhalter ? label : undefined}
      />
    );
    if (platzhalter) return input;
    return (
      <label className="block min-w-0">
        <span className={labelKlasse}>{label}</span>
        {input}
      </label>
    );
  }

  return (
    <div className="space-y-3">
      {firmaName && (
        // Unter 480px untereinander: nebeneinander wurden „Firma (falls zutreffend)“ u. ä. abgeschnitten.
        <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2">
          {firmaName === "betreiber" ? (
            <>
              {feld("company", L.firma)}
              {feld("name", L.name)}
            </>
          ) : (
            <>
              {feld("name", L.name)}
              {feld("company", L.firma)}
            </>
          )}
        </div>
      )}
      {feld("street", L.street)}
      {zeigeZusatz && feld("addition", L.addition)}
      {/* min-w-0: Grid-Items haben ohne das eine implizite min-width:auto (= Inhalt) statt 0 - bei
          zwei Feldern nebeneinander reisst das den Container auf schmalen Handys nach rechts auf
          (dasselbe bereits dokumentierte Mobile-Fix-Muster wie VeranstaltungsDatenDrawer.tsx). */}
      <div className="grid grid-cols-2 gap-3">
        {feld("zip", L.zip)}
        {feld("city", L.city)}
      </div>
      {zeigeLand &&
        (variante === "platzhalter" ? (
          <Select
            ariaLabel={L.land}
            value={werte.countryCode || KEIN_LAND}
            onValueChange={(v) => onFeldChange("countryCode", v === KEIN_LAND ? "" : v)}
            disabled={disabled}
            placeholder={L.land}
            options={[{ value: KEIN_LAND, label: `— ${L.land} wählen —` }, ...(laender ?? []).map((l) => ({ value: l.code, label: l.name }))]}
          />
        ) : (
          <label className="block">
            <span className={labelKlasse}>{L.land}</span>
            <Select
              ariaLabel={L.land}
              value={werte.countryCode || KEIN_LAND}
              onValueChange={(v) => onFeldChange("countryCode", v === KEIN_LAND ? "" : v)}
              disabled={disabled}
              options={[{ value: KEIN_LAND, label: "— nicht angegeben —" }, ...(laender ?? []).map((l) => ({ value: l.code, label: l.name }))]}
            />
          </label>
        ))}
    </div>
  );
}
